<template>
    <div class="rel overflow-y:hidden" :class="content.shorts ? 'aspect:9/16' : 'video'">
        <div class="h:calc(100%+1000px) mt:-500 w:full" :id="id"></div>
        <div class="abs bg:center bg:cover h:full top:0 w:full" @click="click" :style="{ backgroundImage }" v-if="validate">
            <a class="icon icon_play" href="javascript:void(0)" v-if="!playing && controls"></a>
        </div>
        <a class="icon icon_close" @click="emit('remove')" href="javascript:void(0)" v-if="editable"></a>
        <a class="icon icon_sound" :class="{ icon_mute: mute }" @click="mute = !mute" href="javascript:void(0)" v-if="validate && controls"></a>
    </div>
</template>

<style scoped>
    .icon {
        background-color: rgba(255, 255, 255, 0.85);
        background-position: center;
        background-repeat: no-repeat;
        border-radius: 50px;
        display: inline-block;
        height: 24px;
        position: absolute;
        vertical-align: middle;
        width: 24px;
    }

    .icon_close {
        background-image: url(asset/image/icon_close.svg);
        background-size: 10px;
        right: 0.5rem;
        top: 0.5rem;
    }

    .icon_play {
        background-image: url(asset/image/icon_right.svg);
        background-size: 30px;
        background-position: 20px;
        left: calc(50% - 30px);
        top: calc(50% - 30px);
        height: 60px;
        width: 60px;
    }

    .icon_sound {
        background-color: #000000;
        background-image: url(asset/image/icon_sound.svg);
        background-size: 21px;
        bottom: 0.5rem;
        height: 30px;
        left: 0.5rem;
        width: 30px;
    }

    .icon_mute {
        background-image: url(asset/image/icon_mute.svg);
    }
</style>

<script setup>
    const { watchThrottled } = VueUse;

    let handle;
    let player;

    const props = defineProps({
        autoplay: { default: true, type: Boolean },
        content: { required: true, type: Object },
        controls: { default: false, type: Boolean },
        editable: { default: false, type: Boolean },
        mute: { default: true, type: Boolean },
        preview: { default: false, type: Boolean }
    });

    const id = props.content.video_id + "-" + Math.random();

    const backgroundImage = ref(`url(https://i3.ytimg.com/vi/${props.content.video_id}/hqdefault.jpg)`);
    const mute = ref(props.mute);
    const playing = ref(props.autoplay);
    const validate = ref(true);

    const change = (event) => {
        switch (event.data) {
            case 0:
                if (props.content.loop) {
                    replay();
                }
                break;
            case 1:
                backgroundImage.value = null;
                if (!playing.value) {
                    player.pauseVideo();
                }
                break;
        }
    };

    const click = () => {
        if (props.controls) {
            playing.value = !playing.value;
        } else {
            emit("click");
        }
    };

    const destroy = () => {
        if (player) {
            if (handle) {
                clearTimeout(handle);
            }

            player.destroy();
        }
    };

    const emit = defineEmits(["click", "ready", "remove"]);

    const error = () => {
        validate.value = false;
    };

    const init = () => {
        destroy();

        player = new YT.Player(id, {
            events: {
                onError: error,
                onReady: ready,
                onStateChange: change
            },
            height: "100%",
            playerVars: {
                controls: 0,
                disablekb: 1,
                enablejsapi: 1
            },
            videoId: props.content.video_id,
            width: "100%"
        });
    };

    const loop = () => {
        if (player.getCurrentTime() > props.content.end_time) {
            if (props.content.loop) {
                replay();
            } else {
                player.seekTo(props.content.begin_time);
                player.pauseVideo();
                playing.value = false;
            }
        }

        handle = setTimeout(loop, 200);
    };

    const ready = () => {
        if (props.preview) {
            emit("ready", player.getDuration());
        }

        if (mute.value) {
            player.mute();
        } else {
            player.unMute();
        }

        player.seekTo(props.content.begin_time);

        loop();
    };

    const replay = () => {
        player.seekTo(props.content.begin_time);
    };

    onMounted(init);

    onUnmounted(destroy);

    watch(() => props.content.video_id, init);

    watch(mute, () => {
        if (mute.value) {
            player.mute();
        } else {
            player.unMute();
        }
    });

    watch(playing, () => {
        if (playing.value) {
            player.playVideo();
        } else {
            player.pauseVideo();
        }
    });

    if (props.preview) {
        watchThrottled(() => props.content.begin_time, () => {
            player.seekTo(props.content.begin_time);
        }, { throttle: 250 });
    }
</script>
